import React, { useEffect } from "react";

import classNames from "classnames";
import { useField } from "formik";
import { useIntl } from "gatsby-plugin-react-intl";
import PropTypes from "prop-types";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";

import { ReactComponent as IconError18 } from "../../icons/icon/18/a-icon-error-18.svg";

import "./codeVerification.scss";

const CodeVerification = ({
  className = null,
  disabled = false,
  numberOfFields,
  name,
  buttons,
  regex,
}) => {
  const [value, meta, helpers] = useField(name);

  const intl = useIntl();

  useEffect(() => {
    const inputs = document.getElementById(name).getElementsByClassName("form-control");

    if (meta.value) {
      Array.from(meta.value).forEach((item, index) => {
        if (inputs[index]) inputs[index].value = item;
      });

      (inputs[meta.value.length] || inputs[inputs.length - 1]).focus();
    } else if (inputs.length > 0) {
      inputs[0].focus();
    }
  }, []);

  const setValue = () => {
    const inputs = document.getElementById(name).getElementsByClassName("form-control");
    let str = "";

    for (let i = 0; i < numberOfFields; i += 1) {
      str += inputs[i]?.value;
    }

    helpers.setValue(str);
  };

  return (
    <div className={classNames("code-verification", className)} id={name}>
      <Form.Group>
        <InputGroup
          className={classNames("code-verification__inputs", {
            invalid: meta.error && meta.touched,
          })}
          onKeyDown={(e) => {
            const { target } = e;
            const pressDelete = ["Delete", "Backspace"].indexOf(e.key) !== -1;

            if (pressDelete) {
              if (!target.value
                && target.previousElementSibling
                && target.previousElementSibling.tagName.toLowerCase() === "input"
              ) {
                target.previousElementSibling.focus();
              }
            }

            if (new RegExp(regex).test(e.key)) {
              if (target.value) {
                target.value = e.key;
                setValue();
              }

              if (meta.touched) {
                helpers.setTouched(false);
              }
            } else if (!pressDelete) {
              helpers.setTouched(true);
            }
          }}
          onKeyUp={(e) => {
            const { target } = e;

            if (new RegExp(regex).test(e.key)
              && target.value
              && target.nextElementSibling
              && target.nextElementSibling.tagName.toLowerCase() === "input") {
              target.nextElementSibling.focus();
            }
          }}
          onPaste={(e) => {
            const pastedCode = (e.clipboardData || window.clipboardData).getData("text")?.replace(/ /g, "");
            const inputs = document.getElementById(name).getElementsByClassName("form-control");

            Array.from(pastedCode).forEach((item, index) => {
              inputs[index].value = item;
            });

            helpers.setValue(pastedCode);
          }}
          role="presentation"
        >
          {[...Array(numberOfFields).keys()].map((i) => (
            <Form.Control
              key={i}
              type="text"
              name={`${name}Field${i}`}
              id={`${name}Field${i}`}
              maxLength="1"
              onChange={() => {
                setValue();
              }}
              disabled={disabled}
              onBlur={value.onBlur}
              aria-errormessage={`${name}-error`}
              aria-invalid={meta.touched && meta.error}
              aria-label={intl.formatMessage({ id: `form.code_verification.digit_${i}` })}
            />
          ))}
        </InputGroup>
        {meta.touched && meta.error && (
          <Form.Text className="invalid-feedback" id={`${name}-error`}>
            <IconError18 /> {meta.error}
          </Form.Text>
        )}
      </Form.Group>

      <div className="code-verification__buttons">
        {buttons}
      </div>
    </div>

  );
};

CodeVerification.propTypes = {
  buttons: PropTypes.node.isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  name: PropTypes.string.isRequired,
  numberOfFields: PropTypes.number.isRequired,
  regex: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

export default CodeVerification;
